/* eslint-disable no-param-reassign */
import Vue from 'vue';
import { cloneObject } from '@/helpers/utility';

const personnelModule = {
  namespaced: true,

  state: {
    // Personnel data mapping
    personnelMap: {}, // Map of personnel by ID
    instancePersonnel: [], // Current instance personnel array

    // Personnel station assignments for drafts
    pathPersonnelStations: {}, // Personnel to station assignments in draft routes
    personnelWithStationOnDraft: [], // Personnel IDs with station assignments in drafts

    // Personnel assignment state
    personnelToBeAssigned: null, // Personnel selected for assignment to a station
  },

  mutations: {
    SET_INSTANCE_PERSONNEL(state, payload) {
      state.instancePersonnel = payload;
    },

    SET_PERSONNEL_MAP(state, personnels) {
      const personnelMap = {};
      personnels.forEach((el) => {
        personnelMap[el.personnel.id] = el;
      });
      state.personnelMap = personnelMap;
    },

    UPDATE_PERSONNEL_ROUTE_ASSIGNMENT(state, { personnelId, routeId, stationId }) {
      // Update instancePersonnel array
      const targetPersonnelIndex = state.instancePersonnel.findIndex((el) => el.personnel.id === personnelId);
      if (targetPersonnelIndex === -1) return;

      const clonedPersonnel = cloneObject(state.instancePersonnel[targetPersonnelIndex]);
      clonedPersonnel.station.id = stationId;
      clonedPersonnel.route.id = routeId;
      state.instancePersonnel.splice(targetPersonnelIndex, 1, clonedPersonnel);
    },

    UPDATE_PERSONNEL_STATION_ASSIGNMENT(state, { personnelId, station = null, routeId = null, direction = 'TO' }) {
      // Update instancePersonnel array to remove/update station assignment
      const targetPersonnelIndex = state.instancePersonnel.findIndex(
        (el) => el.personnel?.id === personnelId || el.id === personnelId,
      );
      if (targetPersonnelIndex === -1) return;

      const clonedPersonnel = cloneObject(state.instancePersonnel[targetPersonnelIndex]);

      // Determine which station property to update based on direction
      const stationKey = direction === 'TO' ? 'station' : 'returnStation';

      if (station === null) {
        clonedPersonnel[stationKey] = {};
      } else {
        // Ensure the station property exists
        if (!clonedPersonnel[stationKey]) {
          clonedPersonnel[stationKey] = {};
        }
        clonedPersonnel[stationKey] = station;
      }
      if (routeId !== null) {
        // An unassigned rider can have no route yet
        clonedPersonnel.route = { ...(clonedPersonnel.route || {}), id: routeId };
      }

      state.instancePersonnel.splice(targetPersonnelIndex, 1, clonedPersonnel);
    },

    SET_PATH_PERSONNEL_STATIONS(state, payload) {
      state.pathPersonnelStations = payload;
    },

    UPDATE_PATH_PERSONNEL_STATION(state, { personnelId, stationId }) {
      Vue.set(state.pathPersonnelStations, personnelId, stationId);
    },

    REMOVE_PATH_PERSONNEL_STATION(state, personnelId) {
      Vue.delete(state.pathPersonnelStations, personnelId);
    },

    SET_PERSONNEL_WITH_STATION_ON_DRAFT(state, payload) {
      state.personnelWithStationOnDraft = payload;
    },

    ADD_PERSONNEL_TO_STATION_DRAFT(state, { personnelId, stationId }) {
      if (!state.personnelWithStationOnDraft.includes(personnelId)) {
        state.personnelWithStationOnDraft.push(personnelId);
      }
      Vue.set(state.pathPersonnelStations, personnelId, stationId);
    },

    REMOVE_PERSONNEL_FROM_STATION_DRAFT(state, personnelId) {
      const index = state.personnelWithStationOnDraft.indexOf(personnelId);
      if (index !== -1) {
        state.personnelWithStationOnDraft.splice(index, 1);
      }
      Vue.delete(state.pathPersonnelStations, personnelId);
    },

    SET_PERSONNEL_TO_BE_ASSIGNED(state, payload) {
      state.personnelToBeAssigned = payload;
    },

    RESET_PERSONNEL_DATA(state) {
      state.instancePersonnel = [];
      state.personnelMap = {};
      state.pathPersonnelStations = {};
      state.personnelWithStationOnDraft = [];
      state.personnelToBeAssigned = null;
    },
  },

  actions: {
    async fetchInstancePersonnel(_, workgroupId) {
      try {
        // This would be replaced with actual API call
        // const response = await personnelService.getInstancePersonnel(workgroupId);
        // commit('SET_INSTANCE_PERSONNELS', response.data);
        console.log('Fetch instance personnel for workgroup:', workgroupId);
      } catch (error) {
        console.error('Error fetching instance personnel:', error);
      }
    },

    async fetchPersonnelMap(_, workgroupId) {
      try {
        // This would be replaced with actual API call
        // const response = await personnelService.getPersonnelMap(workgroupId);
        // commit('SET_PERSONNEL_MAP', response.data);
        console.log('Fetch personnel map for workgroup:', workgroupId);
      } catch (error) {
        console.error('Error fetching personnel map:', error);
      }
    },

    assignPersonnelToStationDraft({ commit }, { personnelId, stationId, routeId, currentRouteId }) {
      // Remove from current route if different
      if (currentRouteId && currentRouteId !== routeId) {
        commit('REMOVE_PERSONNEL_FROM_STATION_DRAFT', personnelId);
      }

      // Assign to new station/route
      commit('ADD_PERSONNEL_TO_STATION_DRAFT', { personnelId, stationId });
      commit('UPDATE_PERSONNEL_ROUTE_ASSIGNMENT', { personnelId, routeId, stationId });
    },

    removePersonnelFromStation({ commit }, personnelId) {
      commit('REMOVE_PERSONNEL_FROM_STATION_DRAFT', personnelId);
    },

    updatePersonnelRouteAssignment({ commit }, { personnelId, routeId, stationId }) {
      commit('UPDATE_PERSONNEL_ROUTE_ASSIGNMENT', { personnelId, routeId, stationId });
    },

    updatePersonnelByStation({ commit }, { stationId, personnelList, routeId, currentRouteId }) {
      personnelList.forEach((personnelId) => {
        // Remove from current route if different
        if (currentRouteId && currentRouteId !== routeId) {
          commit('REMOVE_PERSONNEL_FROM_STATION_DRAFT', personnelId);
        }

        // Assign to new station/route
        commit('ADD_PERSONNEL_TO_STATION_DRAFT', { personnelId, stationId });
        commit('UPDATE_PERSONNEL_ROUTE_ASSIGNMENT', { personnelId, routeId, stationId });
      });
    },

    updatePersonnelByRouteAndStation({ commit }, { routeId, stationId, personnelList }) {
      personnelList.forEach((personnelId) => {
        commit('UPDATE_PERSONNEL_ROUTE_ASSIGNMENT', { personnelId, routeId, stationId });
      });
    },

    // Bulk operations
    setPersonnelData({ commit }, { instancePersonnel, personnelMap }) {
      if (instancePersonnel) commit('SET_INSTANCE_PERSONNEL', instancePersonnel);
      if (personnelMap) commit('SET_PERSONNEL_MAP', personnelMap);
    },

    clearPersonnelData({ commit }) {
      const mutations = [
        'SET_INSTANCE_PERSONNEL',
        'SET_PERSONNEL_MAP',
        'SET_PATH_PERSONNEL_STATIONS',
        'SET_PERSONNEL_WITH_STATION_ON_DRAFT',
      ];
      const values = [[], {}, {}, []];
      mutations.forEach((mutation, index) => commit(mutation, values[index]));
    },

    resetPersonnelData({ commit }) {
      commit('RESET_PERSONNEL_DATA');
    },

    clearPersonnelDraftStationAssignments({ commit }) {
      commit('SET_PERSONNEL_WITH_STATION_ON_DRAFT', []);
      commit('SET_PATH_PERSONNEL_STATIONS', {});
    },

    setAssignPersonnel({ commit, rootState }, personnel) {
      commit('SET_PERSONNEL_TO_BE_ASSIGNED', personnel);
      // Set route edit action mode to assign personnel mode
      // When clearing personnel (null), return to previous mode (edit if in edit mode, otherwise view)
      if (personnel) {
        commit('SET_ROUTE_EDIT_ACTION_MODE', 'assignPersonnel', { root: true });
      } else {
        const isEditMode = rootState.routeList?.isEditMode || false;
        const targetMode = isEditMode ? 'edit' : 'view';
        commit('SET_ROUTE_EDIT_ACTION_MODE', targetMode, { root: true });
      }
    },

    /**
     * Update instance personnel data across both routeList and routePersonnel modules
     * This ensures consistency across all components that read personnel data
     *
     * @param {Object} context - Vuex action context
     * @param {Array} personnel - Array of personnel objects from API
     */
    updateInstancePersonnel({ commit }, personnel) {
      if (!personnel || !Array.isArray(personnel)) {
        console.warn('[routePersonnel/updateInstancePersonnel] Invalid personnel data provided');
        return;
      }

      // Update routePersonnel module (new/current module)
      commit('SET_INSTANCE_PERSONNEL', personnel);
      commit('SET_PERSONNEL_MAP', personnel);

      // Update routeList module (legacy module for backward compatibility)
      commit('SET_INSTANCE_PERSONNELS', personnel, { root: true });
      commit('SET_PERSONNEL_MAP', personnel, { root: true });
    },
  },

  getters: {
    // Get personnel by route ID
    getPersonnelByRoute: (state) => (routeId) => {
      if (!state.instancePersonnel || !Array.isArray(state.instancePersonnel)) return [];
      return state.instancePersonnel.filter((p) => p?.route?.id === routeId);
    },

    // Get personnel by station ID
    getPersonnelByStation: (state) => (stationId) => {
      if (!state.instancePersonnel || !Array.isArray(state.instancePersonnel)) return [];
      return state.instancePersonnel.filter((p) => p?.station?.id === stationId);
    },

    // Get personnel without route assignment
    personnelWithoutRoute: (state, getters, rootState, rootGetters) => {
      const { workgroupType } = rootGetters;
      const { personnelByRoute } = getters;

      // Get personnel without routes (grouped under null key)
      const personnelWithoutRoutes = personnelByRoute.null || [];

      // For SHUTTLE workgroups, return all personnel without routes
      if (workgroupType === 'SHUTTLE') {
        return personnelWithoutRoutes;
      }

      // For other workgroups (VANPOOL, etc.), filter out vanpool drivers
      return personnelWithoutRoutes.filter((p) => !p.personnel?.isVanpoolDriver);
    },

    // Get personnel grouped by route
    personnelByRoute(state) {
      const routes = {};
      // Add null check for instancePersonnel
      if (!state.instancePersonnel || !Array.isArray(state.instancePersonnel)) {
        return routes;
      }

      state.instancePersonnel.forEach((p) => {
        // Group null, undefined, and 0 route IDs together under 'null'
        const routeId = p?.route?.id || null;
        routes[routeId] = routes[routeId] || [];
        routes[routeId].push(p);
      });
      return routes;
    },
    // Get personnel grouped by station within routes
    personnelByStation: (state, getters, rootState) => {
      const routes = {};
      const { allRoutes = [] } = rootState.routeList;

      allRoutes.forEach((route) => {
        if (!routes[route.id]) routes[route.id] = {};
        const { stations = [] } = route;

        stations.forEach((station) => {
          routes[route.id][station.id] = state.instancePersonnel.filter(
            (p) => p.route.id === route.id && p.station.id === station.id,
          );
        });
      });

      return routes;
    },

    // Get personnel with station assignments on draft routes
    personnelWithStationOnDraft: (state, getters, rootState) => {
      const draftRoutes = rootState.routeList.draftRoutes || {};
      const routeIds = Object.keys(draftRoutes);

      return routeIds
        .map((rid) => {
          const pathPersonnelStations = draftRoutes[rid]?.draftVersion?.routeDetail?.pathPersonnelStations || {};
          return Object.keys(pathPersonnelStations);
        })
        .flat();
    },

    // Get personnel by ID
    getPersonnelById: (state) => (personnelId) => state.personnelMap[personnelId] || null,

    // Get personnel by route ID (using computed getter instead of state)
    personnelByRouteId: (state, getters) => (routeId) => getters.personnelByRoute[routeId] || [],

    // Get all personnel IDs
    allPersonnelIds: (state) => Object.keys(state.personnelMap),

    // Get personnel count by route
    getPersonnelCountByRoute: (state, getters) => (routeId) => {
      const personnel = getters.getPersonnelByRoute(routeId);
      return personnel ? personnel.length : 0;
    },

    // Get personnel count by station
    getPersonnelCountByStation: (state, getters) => (stationId) => {
      const personnel = getters.getPersonnelByStation(stationId);
      return personnel ? personnel.length : 0;
    },

    // Check if personnel is assigned to a route
    isPersonnelAssignedToRoute: (state) => (personnelId) => {
      const personnel = state.personnelMap[personnelId];
      return personnel && personnel.route && personnel.route.id;
    },

    // Check if personnel is assigned to a station
    isPersonnelAssignedToStation: (state) => (personnelId) => {
      const personnel = state.personnelMap[personnelId];
      return personnel && personnel.station && personnel.station.id;
    },

    // Get personnel to be assigned
    personnelToBeAssigned: (state) => state.personnelToBeAssigned,

    // Check if personnel details should be visible to current user
    // For SERVICE_ADMIN users, visibility depends on company settings
    // For other users, personnel details are always visible
    isPersonnelVisible: (state, getters, rootState) => {
      const { userDetails, companySettings } = rootState;
      const isServiceAdmin = userDetails?.accountType === 'SERVICE_ADMIN';

      if (!isServiceAdmin) {
        return true; // Non-service admins can always see personnel details
      }

      // For SERVICE_ADMIN, check company settings
      return companySettings?.employeeListVisibleToShuttleProviders === true;
    },

    // Check if account should be visible to current user
    // For SERVICE_ADMIN users, visibility depends on permission
    // For other users, account is always visible
    isAccountVisible: (state, getters, rootState, rootGetters) => {
      const { userDetails } = rootState;
      const isServiceAdmin = userDetails?.accountType === 'SERVICE_ADMIN';

      if (!isServiceAdmin) {
        return true; // Non-service admins can always see account
      }

      // For SERVICE_ADMIN, check permissions
      return rootGetters.hasPermission('routeAccounting:view');
    },
  },
};

export default personnelModule;
